/**
 * Advanced Question Bank: Domain 4 — Verbal & Linguistic Reasoning (الاستدلال اللفظي والدلالي)
 * 15 Psychometrically Rigorous Generative Template Families (Crystallized Intelligence Gc / Formal Logic)
 */

export const ADVANCED_VERBAL_TEMPLATES = [
  // 1. Cross-Domain Functional Analogy
  {
    id: 'verbal_cross_domain_analogy',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const analogies = [
        {
          pair1_ar: 'النواة : الخلية',
          pair1_en: 'Nucleus : Cell',
          stem_ar: 'المعالج المركزي (CPU) : ؟',
          stem_en: 'Central Processing Unit (CPU) : ?',
          target_ar: 'الحاسوب',
          target_en: 'Computer',
          distractors_ar: ['الشاشة', 'الكهرباء', 'البرمجيات'],
          distractors_en: ['Monitor', 'Electricity', 'Software'],
          relation_ar: 'علاقة المركز الإداري التشغيلي الذي يتحكم ويوجه كامل المنظومة الحيوية أو التقنية.',
          relation_en: 'The operational command center that controls and directs the entire biological or computational system.'
        },
        {
          pair1_ar: 'الدفة : السفينة',
          pair1_en: 'Rudder : Ship',
          stem_ar: 'المقود (عجلة القيادة) : ؟',
          stem_en: 'Steering Wheel : ?',
          target_ar: 'السيارة',
          target_en: 'Car',
          distractors_ar: ['المحرك', 'الطريق', 'الفرامل'],
          distractors_en: ['Engine', 'Road', 'Brakes'],
          relation_ar: 'علاقة أداة التوجيه وتحديد المسار بالمركبة التي توجهها.',
          relation_en: 'The directional control mechanism paired with the vehicle it steers.'
        },
        {
          pair1_ar: 'البوصلة : الملاحة',
          pair1_en: 'Compass : Navigation',
          stem_ar: 'المجهر : ؟',
          stem_en: 'Microscope : ?',
          target_ar: 'الفحص المجهري (البيولوجيا)',
          target_en: 'Microscopy (Biology)',
          distractors_ar: ['العدسة', 'المخبر', 'الضوء'],
          distractors_en: ['Lens', 'Laboratory', 'Light'],
          relation_ar: 'علاقة الأداة التخصصية بالمجال العلمي التطبيقي الذي تُستخدم فيه.',
          relation_en: 'The specialized instrument paired with the scientific application domain it serves.'
        }
      ];

      const item = prng.pick(analogies);
      const all = prng.shuffle([
        { ar: item.target_ar, en: item.target_en, isCorrect: true },
        ...item.distractors_ar.map((d, i) => ({ ar: d, en: item.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أكمل التناظر اللفظي الوظيفي التالي بنفس العلاقة المنطقية الرابطة:\n\n[ ${item.pair1_ar} ]  مثل  [ ${item.stem_ar} ]`,
        questionText_en: `Complete the functional verbal analogy based on the underlying systematic relationship:\n\n[ ${item.pair1_en} ]  is as  [ ${item.stem_en} ]`,
        options,
        correctIndex,
        explanation_ar: `العلاقة هي: ${item.relation_ar} لذلك فإن النظير الصحيح هو "${item.target_ar}" (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The underlying relationship is: ${item.relation_en} Identifying "${item.target_en}" establishes (${options[correctIndex].id}).`
      };
    }
  },

  // 2. Formal Categorical Syllogism Validity
  {
    id: 'verbal_syllogism_validity',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const syllogisms = [
        {
          p1_ar: 'كل الفلاسفة باحثون عن الحقيقة.',
          p2_ar: 'بعض الباحثين عن الحقيقة لا يهتمون بالماديات.',
          c_ar: 'بعض الباحثين عن الحقيقة فلاسفة بالضرورة.',
          valid_ar: 'الاستنتاج يتبع منطقيًا بالضرورة (Valid Deduction)',
          valid_en: 'Logically Valid Deduction',
          dist_ar: ['الاستنتاج غير صحيح ومغالط (Invalid Fallacy)', 'المقدمات متناقضة مع بعضها', 'لا يمكن الحكم لعدم كفاية المعلومات'],
          dist_en: ['Logically Invalid Fallacy', 'Contradictory Premises', 'Indeterminate Information'],
          exp_ar: 'إذا كان كل (أ) ينتمي إلى (ب)، فإن العكس الجزئي صحيح بالضرورة: بعض (ب) هو (أ) حتمًا.'
        },
        {
          p1_ar: 'لا أحد من الزواحف يمتلك ريشًا.',
          p2_ar: 'جميع الطيور تمتلك ريشًا.',
          c_ar: 'إذن: لا أحد من الطيور من الزواحف.',
          valid_ar: 'الاستنتاج يتبع منطقيًا بالضرورة (Valid Deduction)',
          valid_en: 'Logically Valid Deduction',
          dist_ar: ['الاستنتاج غير صحيح ومغالط (Invalid Fallacy)', 'المقدمات غير واقعية', 'النتيجة احتمالية وليست يقينية'],
          dist_en: ['Logically Invalid Fallacy', 'Unrealistic Premises', 'Probabilistic only'],
          exp_ar: 'مجموعتان منفصلتان تمامًا (الريش وعدم الريش)، إذن تقاطع الطيور مع الزواحف هو المجموعة الخالية ∅.'
        }
      ];

      const chosen = prng.pick(syllogisms);
      const all = prng.shuffle([
        { ar: chosen.valid_ar, en: chosen.valid_en, isCorrect: true },
        ...chosen.dist_ar.map((d, i) => ({ ar: d, en: chosen.dist_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بافتراض صدق المقدمات المنطقية التالية تمامًا:\n• المقدمة 1: ${chosen.p1_ar}\n• المقدمة 2: ${chosen.p2_ar}\n\nالنتيجة المطروحة: "${chosen.c_ar}"\n\nما هو التقييم المنطقي الصارم لهذه النتيجة؟`,
        questionText_en: `Assuming the following logical premises are strictly true:\n• Premise 1: ${chosen.p1_ar}\n• Premise 2: ${chosen.p2_ar}\n\nProposed Conclusion: "${chosen.c_ar}"\n\nWhat is the rigorous logical evaluation of this conclusion?`,
        options,
        correctIndex,
        explanation_ar: `${chosen.exp_ar} الاستنتاج صحيح منطقيًا ولازم عن المقدمات لزومًا قطعيًا (الخيار ${options[correctIndex].id}).`,
        explanation_en: `The deduction holds with deductive necessity under predicate logic, validating option (${options[correctIndex].id}).`
      };
    }
  },

  // 3. Nuanced Semantic Antonym Subtlety
  {
    id: 'verbal_antonym_subtlety',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const pairs = [
        {
          word_ar: 'الإيثار (Altruism)',
          word_en: 'Altruism',
          antonym_ar: 'الأَثَرَة والأنانية (Selfish Egoism)',
          antonym_en: 'Selfish Egoism',
          distractors_ar: ['البخل والشح', 'العدوانية والعنف', 'التواضع والزهد'],
          distractors_en: ['Miserliness', 'Aggression', 'Humility'],
          exp_ar: 'الإيثار هو تقديم مصلحة الغير على النفس، وضده الدلالي المباشر هو "الأَثَرَة" وهي حب النفس وتفضيلها على الآخرين.'
        },
        {
          word_ar: 'السرمدي / الأبدي (Eternal)',
          word_en: 'Eternal',
          antonym_ar: 'الزائل / المؤقت الفاني (Ephemeral)',
          antonym_en: 'Ephemeral',
          distractors_ar: ['القديم العتيق', 'المتغير والمتحول', 'المجهول الغامض'],
          distractors_en: ['Ancient', 'Variable', 'Mysterious'],
          exp_ar: 'السرمدي هو ما لا نهاية لوجوده في الزمان، وضده الدقيق هو "الزائل أو الفاني" وهو ما ينقضي وجوده في أمد قصير.'
        },
        {
          word_ar: 'المُقتصد (Parsimonious)',
          word_en: 'Parsimonious',
          antonym_ar: 'المُسرف والمُبذر (Prodigal / Lavish)',
          antonym_en: 'Prodigal / Lavish',
          distractors_ar: ['الغني الثري', 'الكريم السخي', 'الطماع الجشع'],
          distractors_en: ['Wealthy', 'Generous', 'Greedy'],
          exp_ar: 'المقتصد أو الشحيح في الإنفاق ضده المباشر على ميزان المبالغة هو "المسرف أو المبذر".'
        }
      ];

      const item = prng.pick(pairs);
      const all = prng.shuffle([
        { ar: item.antonym_ar, en: item.antonym_en, isCorrect: true },
        ...item.distractors_ar.map((d, i) => ({ ar: d, en: item.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الضد الدلالي الدقيق والأشمل لمفهوم: "${item.word_ar}"؟`,
        questionText_en: `What is the precise and most rigorous semantic antonym for: "${item.word_en}"?`,
        options,
        correctIndex,
        explanation_ar: `${item.exp_ar} الخيار الدقيق هو (${options[correctIndex].id}).`,
        explanation_en: `Identifying the conceptual polar opposite yields (${options[correctIndex].id}).`
      };
    }
  },

  // 4. Strict Taxonomic Hierarchy (Hyponym & Hypernym)
  {
    id: 'verbal_semantic_hypernym',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const taxonomies = [
        {
          items_ar: 'النسر ، الصقر ، الشاهين ، العقاب',
          items_en: 'Eagle, Falcon, Peregrine, Hawk',
          category_ar: 'الطيور الجارحة (Raptors)',
          category_en: 'Birds of Prey (Raptors)',
          distractors_ar: ['الطيور المهاجرة', 'الفقاريات المائية', 'آكلات الحبوب'],
          distractors_en: ['Migratory Birds', 'Aquatic Vertebrates', 'Granivores'],
          exp_ar: 'تشترك جميع هذه الطيور في تصنيف بيولوجي تخصصي دقيق هو "الطيور الجارحة" التي تعتمد على الصيد واللحوم بمخالب معقوفة.'
        },
        {
          items_ar: 'البرونز ، الفولاذ ، النحاس الأصفر (الشبه) ، البوتر',
          items_en: 'Bronze, Steel, Brass, Pewter',
          category_ar: 'السبائك المعدنية المخلوطة (Alloys)',
          category_en: 'Metallic Alloys',
          distractors_ar: ['العناصر الكيميائية النقية', 'المعادن القلوية', 'المركبات العضوية'],
          distractors_en: ['Pure Elements', 'Alkali Metals', 'Organic Compounds'],
          exp_ar: 'جميع هذه المواد ليست عناصر كيميائية مفردة في الجدول الدوري، بل هي "سبائك معدنية" مصهورة من مزيج فلزين أو أكثر.'
        }
      ];

      const item = prng.pick(taxonomies);
      const all = prng.shuffle([
        { ar: item.category_ar, en: item.category_en, isCorrect: true },
        ...item.distractors_ar.map((d, i) => ({ ar: d, en: item.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو التصنيف الجامع الشامل (Hypernym) الذي يضم المفردات التالية تحت مظلة فئوية واحدة علميًا؟\n\n[ ${item.items_ar} ]`,
        questionText_en: `What is the precise overarching hypernym category that scientifically encompasses all the following terms?\n\n[ ${item.items_en} ]`,
        options,
        correctIndex,
        explanation_ar: `${item.exp_ar} الخيار الجامع الصحيح هو (${options[correctIndex].id}).`,
        explanation_en: `The overarching taxonomic superordinate category is strictly (${options[correctIndex].id}).`
      };
    }
  },

  // 5. Logical Fallacy Identification
  {
    id: 'verbal_logical_fallacy_id',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const fallacies = [
        {
          quote_ar: '"يجب أن نقبل بنظرية هذا العالم في الفلك، لأنه شخص طيب القلب ويتبرع بكامل أمواله للجمعيات الخيرية."',
          quote_en: '"We must accept this scientist\'s astrophysical theory because he is a generous man who donates all his money to charity."',
          fallacy_ar: 'مغالطة الاحتكام إلى المشاعر / الشفقة (Appeal to Emotion)',
          fallacy_en: 'Appeal to Emotion / Irrelevant Appeal',
          distractors_ar: ['مغالطة رجل القش', 'مغالطة المنحدر الزلق', 'المصادرة على المطلوب'],
          distractors_en: ['Straw Man Fallacy', 'Slippery Slope', 'Begging the Question'],
          exp_ar: 'الاستدلال يخلط بين السمات الأخلاقية للشخص وصحة حجته العلمية التجريبية، وهي مغالطة استدلال واضحة.'
        },
        {
          quote_ar: '"إما أن توافق على كل بند في هذه الخطة بدون أي تعديل، أو أنك تريد للمشروع بأكمله أن يفشل وينهدم."',
          quote_en: '"Either you agree with every single clause in this proposal without modification, or you want the entire project to fail catastrophically."',
          fallacy_ar: 'مغالطة الإحراج الزائف / الورطة الكاذبة (False Dilemma)',
          fallacy_en: 'False Dilemma / False Dichotomy',
          distractors_ar: ['مغالطة الشخصنة (Ad Hominem)', 'مغالطة رجل القش (Straw Man)', 'مغالطة التعميم المتسرع'],
          distractors_en: ['Ad Hominem', 'Straw Man', 'Hasty Generalization'],
          exp_ar: 'الاستدلال يحصر الخيارات تعسفًا في طرفين نقيضين فقط متجاهلاً الحلول الوسطى والتعديلات التوفيقية.'
        }
      ];

      const item = prng.pick(fallacies);
      const all = prng.shuffle([
        { ar: item.fallacy_ar, en: item.fallacy_en, isCorrect: true },
        ...item.distractors_ar.map((d, i) => ({ ar: d, en: item.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `اقرأ الحجة التالية وحدد المغالطة المنطقية الصريحة التي وقع فيها المتحدث:\n\n${item.quote_ar}`,
        questionText_en: `Read the argument below and identify the formal or informal logical fallacy committed:\n\n${item.quote_en}`,
        options,
        correctIndex,
        explanation_ar: `${item.exp_ar} المغالطة المنطقية المرتكبة هي (${options[correctIndex].id}).`,
        explanation_en: `The reasoning defect matches the diagnostic definition of (${options[correctIndex].id}).`
      };
    }
  },

  // 6. Propositional Logic Contrapositive
  {
    id: 'verbal_modal_contraposition',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      // P -> Q <=> ~Q -> ~P
      const rule = {
        prop_ar: 'إذا نزل المطر بغزارة (P)، فإن الشوارع تصبح مبللة (Q).',
        prop_en: 'If it rains heavily (P), then the streets are wet (Q).',
        contra_ar: 'إذا لم تكن الشوارع مبللة (~Q)، فمن المؤكد أن المطر لم ينزل بغزارة (~P).',
        contra_en: 'If the streets are not wet (~Q), then it definitely did not rain heavily (~P).',
        d1_ar: 'إذا كانت الشوارع مبللة، فإن المطر نزل بغزارة بالتأكيد.',
        d1_en: 'If the streets are wet, then it definitely rained heavily.',
        d2_ar: 'إذا لم ينزل المطر بغزارة، فإن الشوارع لن تكون مبللة إطلاقًا.',
        d2_en: 'If it does not rain heavily, then the streets will never be wet.',
        d3_ar: 'المطر ينزل بغزارة في كل مرة تجف فيها الشوارع.',
        d3_en: 'Rain falls heavily whenever the streets dry out.'
      };

      const options = [
        { id: 'A', text_ar: rule.contra_ar, text_en: rule.contra_en, isCorrect: true },
        { id: 'B', text_ar: rule.d1_ar, text_en: rule.d1_en, isCorrect: false },
        { id: 'C', text_ar: rule.d2_ar, text_en: rule.d2_en, isCorrect: false },
        { id: 'D', text_ar: rule.d3_ar, text_en: rule.d3_en, isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `وفقًا لقواعد المنطق الرياضي الصارم، ما هي العبارة المكافئة منطقيًا بنسبة 100% (المعاكس الإيجابي Contrapositive) للقضية الشرطية:\n\n"${rule.prop_ar}"؟`,
        questionText_en: `Under formal propositional logic, which proposition represents the strictly equivalent Contrapositive (~Q -> ~P) of the conditional statement:\n\n"${rule.prop_en}"?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `القاعدة المنطقية تنص على أن القضية الشرطية (إذا كان أ فإن ب) تكافئ دائمًا ويقينًا معاكسها الإيجابي (إذا لم يكن ب فإن أ لم يكن). هذا يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `The law of contraposition states (P -> Q) <=> (~Q -> ~P). This isolates option (${shuffled[correctIndex].id}) with mathematical certainty.`
      };
    }
  },

  // 7. Semantic Intensity Graduation Scale
  {
    id: 'verbal_degree_intensity_scale',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const scales = [
        {
          prompt_ar: 'دافئ ⬅️ حار ⬅️ لاهب ⬅️ ؟',
          prompt_en: 'Warm ⬅️ Hot ⬅️ Scorching ⬅️ ?',
          target_ar: 'مُستعر / جحيمي',
          target_en: 'Infernal / Incandescent',
          distractors_ar: ['بارد', 'فاتر', 'مُعتدل'],
          distractors_en: ['Cold', 'Lukewarm', 'Mild'],
          exp_ar: 'المتتالية تتدرج في شدة الحرارة تصاعديًا من الأقل للأعلى، واللفظ الأشد بعد "اللاهب" هو "المستعر".'
        },
        {
          prompt_ar: 'همس ⬅️ حديث ⬅️ هتاف ⬅️ ؟',
          prompt_en: 'Whisper ⬅️ Speech ⬅️ Shout ⬅️ ?',
          target_ar: 'صراخ / صاعق',
          target_en: 'Screech / Clamor',
          distractors_ar: ['سكوت', 'إشارة', 'تمتمة'],
          distractors_en: ['Silence', 'Gesture', 'Mumble'],
          exp_ar: 'المتتالية تترقى تصاعديًا في شدة الصوت والجهارة، والحد الأقصى في السلم الصوتي هو "الصراخ".'
        }
      ];

      const chosen = prng.pick(scales);
      const all = prng.shuffle([
        { ar: chosen.target_ar, en: chosen.target_en, isCorrect: true },
        ...chosen.distractors_ar.map((d, i) => ({ ar: d, en: chosen.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تتدرج الألفاظ التالية على مقياس دلالي تصاعدي من الأضعف شدة إلى الأقوى شدة. ما هي الكلمة التي تمثل ذروة الشدة في نهاية المقياس؟\n\n[ ${chosen.prompt_ar} ]`,
        questionText_en: `The following vocabulary items progress along a strict scale of escalating semantic intensity. Which word represents the apex intensity at the end of the scale?\n\n[ ${chosen.prompt_en} ]`,
        options,
        correctIndex,
        explanation_ar: `${chosen.exp_ar} الكلمة المكملة للذروة هي (${options[correctIndex].id}).`,
        explanation_en: `Ascending the intensity gradient identifies (${options[correctIndex].id}) as the semantic peak.`
      };
    }
  },

  // 8. Arabic Morphological Root Cognate Pattern
  {
    id: 'verbal_etymological_cognate',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const items = [
        {
          root_ar: 'جذر (ك - ت - ب): كاتب ، مكتوب ، كتابة ، مكتب',
          weight_ar: 'مِفْعَال (اسم آلة أو صيغة مبالغة)',
          target_ar: 'مِكْتَاب (آلة الكتابة أو كثير الكتابة)',
          target_en: 'Miktāb',
          distractors_ar: ['مُكتئب', 'كبت', 'كتيبة'],
          distractors_en: ['Depressed', 'Suppressed', 'Battalion'],
          exp_ar: 'الكلمة التي تشترك في نفس الجذر اللغوي الصرفي الدقيق (ك-ت-ب) وتأتي على وزن مِفْعَال هي "مِكْتَاب"، بينما الكلمات الأخرى مشتقة من جذور مغايرة كليًا (ك-أ-ب أو ك-ب-ت).'
        },
        {
          root_ar: 'جذر (ع - ل - م): عالم ، معلوم ، تعليم ، علامة',
          weight_ar: 'فَعَّال (صيغة مبالغة)',
          target_ar: 'عَلاَّم',
          target_en: 'Allām',
          distractors_ar: ['عَمَالقة', 'مُعلَّق', 'عالمي'],
          distractors_en: ['Giants', 'Suspended', 'Global'],
          exp_ar: 'صيغة المبالغة القياسية على وزن فَعَّال من جذر (ع-ل-م) هي "عَلاَّم".'
        }
      ];

      const chosen = prng.pick(items);
      const all = prng.shuffle([
        { ar: chosen.target_ar, en: chosen.target_en, isCorrect: true },
        ...chosen.distractors_ar.map((d, i) => ({ ar: d, en: chosen.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على الاشتقاق الصرفي وقوانين الميزان الصرفي في اللغة العربية:\nأي الكلمات التالية تشتق اشتقاقًا مباشرًا أصيلاً من نفس ${chosen.root_ar} على الوزن الصرفي [ ${chosen.weight_ar} ]؟`,
        questionText_en: `Based on Arabic root morphology and semantic derivational paradigms:\nWhich word derives directly from ${chosen.root_ar} fitting the specific morphological pattern [ ${chosen.weight_ar} ]?`,
        options,
        correctIndex,
        explanation_ar: `${chosen.exp_ar} الخيار المطابق تمامًا هو (${options[correctIndex].id}).`,
        explanation_en: `Morphological parsing isolates the exact root-cognate match in (${options[correctIndex].id}).`
      };
    }
  },

  // 9. Causal Consequence Chain Deduction
  {
    id: 'verbal_cause_consequence_chain',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'ارتفاع أسعار الخبز والمواد الغذائية المشتقة', text_en: 'Inflation in bread and grain-derived food products', isCorrect: true },
        { id: 'B', text_ar: 'انخفاض درجات الحرارة العالمية فورًا', text_en: 'Immediate drop in global temperatures', isCorrect: false },
        { id: 'C', text_ar: 'زيادة مساحات الغابات الاستوائية المطيرة', text_en: 'Expansion of tropical rainforest surface area', isCorrect: false },
        { id: 'D', text_ar: 'توقف حركة الملاحة الجوية الدولية بالكامل', text_en: 'Total suspension of international air travel', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `تأمل سلسلة الأسباب والنتائج المتتالية التالية:\n(1) حدوث جفاف شديد ⬅️ (2) تلف محاصيل القمح والحبوب ⬅️ (3) انخفاض حاد في المعروض العالمي من الحبوب ⬅️ (4) ؟\n\nما هي النتيجة الاقتصادية الحتمية والمباشرة في الحلقة الرابعة من السلسلة؟`,
        questionText_en: `Evaluate the sequential causal chain:\n(1) Severe Drought ⬅️ (2) Crop Blight & Failure ⬅️ (3) Acute Deficit in Grain Supply ⬅️ (4) ?\n\nWhat is the direct, necessary economic consequence completing the fourth link of the chain?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `وفق قانون العرض والطلب الاقتصادي والسببي المباشر، فإن الانخفاض الحاد في معروض سلعة أساسية كالحبوب يؤدي بالضرورة المباشرة إلى ارتفاع أسعار المنتجات المعتمدة عليها، وهو ما يحدده الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Direct causal mechanics under supply-demand equilibrium dictate immediate price inflation for derived staples, isolating (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 10. Proverbial Thematic Isomorphism
  {
    id: 'verbal_proverbial_isomorphism',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'فاقد الشيء لا يعطيه', text_en: 'You cannot give what you do not have', isCorrect: true },
        { id: 'B', text_ar: 'لكل جواد كبوة ولكل صارم نبوة', text_en: 'Every horse may stumble', isCorrect: false },
        { id: 'C', text_ar: 'رب رمية من غير رام', text_en: 'A fortunate shot without an archer', isCorrect: false },
        { id: 'D', text_ar: 'على نفسها جنت براقش', text_en: 'Baraqish brought ruin upon herself', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الأمثال والحكم التالية يحمل نفس الدلالة المعنوية والمجازية العميقة للمثل اللاتيني القائل: "لا يستطيع الجدول أن يفيض بماءٍ لم يتلقّه من نبعه"؟`,
        questionText_en: `Which classical proverb shares the identical core thematic metaphor with the maxim: "A stream cannot overflow with water it never received from its source"?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `كلا القولين يؤكدان على نفس الفكرة الجوهرية: أن الكيان لا يمكنه منح أو إظهار صفة أو عطاء يفتقر إليه في أصله وتكوينه، وهو المعنى المطابق للمثل "فاقد الشيء لا يعطيه" (الخيار ${shuffled[correctIndex].id}).`,
        explanation_en: `Both idioms convey the identical principle of ontological insufficiency: an entity cannot bestow that which it does not possess, matching (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 11. Necessary vs Sufficient Condition Discrimination
  {
    id: 'verbal_sine_qua_non_condition',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'وجود الأكسجين شرط لازم (ضروري) لكنه ليس كافيًا بمفرده لحدوث الاشتعال', text_en: 'Oxygen presence is a necessary condition, but not sufficient alone for combustion', isCorrect: true },
        { id: 'B', text_ar: 'وجود الأكسجين شرط كافٍ بمفرده لحدوث الاشتعال دون الحاجة لحرارة أو وقود', text_en: 'Oxygen alone is a sufficient condition to cause ignition without heat or fuel', isCorrect: false },
        { id: 'C', text_ar: 'وجود الأكسجين ليس شرطًا ضروريًا ولا شرطًا كافيًا', text_en: 'Oxygen is neither a necessary nor a sufficient condition', isCorrect: false },
        { id: 'D', text_ar: 'الاشتعال هو الشرط اللازم لوجود الأكسجين في الكون', text_en: 'Combustion is the necessary condition for oxygen existence', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في دراسة شروط حدوث تفاعل الاحتراق الكيميائي، تتطلب النار وجود: وقود، وحرارة، وأكسجين.\nما هو الوصف المنطقي الصارم لدور "الأكسجين" في هذه العلاقة؟`,
        questionText_en: `In formal logic analysis of chemical combustion requiring fuel, heat, and oxygen:\nWhat is the rigorous logical designation of "oxygen" in this causal relationship?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الأكسجين "شرط لازم" (ضروري Necessary) لأنه بدون وجوده يستحيل حدوث الاحتراق، لكنه "ليس كافيًا" (Not Sufficient) بمفرده لأن وجود الأكسجين في الهواء لا يشعل حريقًا تلقائيًا دون وقود ومصدر حرارة. الخيار الدقيق هو (${shuffled[correctIndex].id}).`,
        explanation_en: `Oxygen is necessary because fire cannot occur in its absence, but insufficient alone because combustion additionally mandates heat and fuel, strictly validating (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 12. Epistemological Knights & Knaves Logic Puzzle
  {
    id: 'verbal_epistemological_deduction',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      // Person A says: "We are both liars."
      // If A is truth-teller -> statement is true -> A is a liar (contradiction).
      // Therefore, A MUST be a liar. Since statement is false, B is NOT a liar -> B is a truth-teller!
      const options = [
        { id: 'A', text_ar: 'الشخص (أ) كاذب دائمًا، والشخص (ب) صادق دائمًا', text_en: 'Person A is a knave (liar), and Person B is a knight (truth-teller)', isCorrect: true },
        { id: 'B', text_ar: 'كلاهما صادق دائمًا ويقولان الحقيقة', text_en: 'Both persons are knights telling the truth', isCorrect: false },
        { id: 'C', text_ar: 'كلاهما كاذب دائمًا', text_en: 'Both persons are knaves (liars)', isCorrect: false },
        { id: 'D', text_ar: 'الشخص (أ) صادق، والشخص (ب) كاذب', text_en: 'Person A is a knight, and Person B is a knave', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في جزيرة يسكنها صنفان فقط: "الصادقون" الذين يقولون الصدق دائمًا، و"الكاذبون" الذين يكذبون دائمًا.\nالتقيت بشخصين (أ) و (ب)، فقال لك الشخص (أ): "كلانا كاذبان".\nما هي الحقيقة المنطقية الحتمية لهوية الشخصين؟`,
        questionText_en: `On an island where inhabitants are strictly either Knights (who always tell the truth) or Knaves (who always lie):\nYou meet two inhabitants, A and B. Inhabitant A states: "Both of us are knaves."\nWhat is the definitive logical identity of A and B?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `لو كان (أ) صادقًا لكانت عبارته صحيحة، مما يعني أنه كاذب، وهذا تناقض منطقي مستحيل! إذن (أ) كاذب حتمًا. وبما أن عبارته كاذبة، فليس كلاهما كاذبين، مما يثبت بالضرورة أن (ب) صادق. الخيار الحتمي هو (${shuffled[correctIndex].id}).`,
        explanation_en: `If A were a knight, his statement would be true, implying he is a knave—a fatal contradiction. Hence A is a knave. Since his statement is false, they are not both knaves, mandating that B is a knight, proving (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 13. Semantic Categorical Exclusion (Ontological Oddity)
  {
    id: 'verbal_categorical_exclusion',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const sets = [
        {
          target_ar: 'الجاذبية',
          target_en: 'Gravity',
          distractors_ar: ['الذهب', 'الماء', 'الحديد'],
          distractors_en: ['Gold', 'Water', 'Iron'],
          exp_ar: 'الذهب والماء والحديد كلها كيانات مادية ملموسة ذات كتلة وحجم (مادة Matter)، بينما الجاذبية هي قوة فيزيائية أساسية ومجال تفاعل وليست مادة ملموسة.'
        },
        {
          target_ar: 'المربع',
          target_en: 'Square',
          distractors_ar: ['المكعب', 'الهرم', 'الكرة'],
          distractors_en: ['Cube', 'Pyramid', 'Sphere'],
          exp_ar: 'المكعب والهرم والكرة كلها مجسمات ثلاثية الأبعاد (3D)، بينما المربع شكل هندسي مسطح ثنائي الأبعاد (2D).'
        }
      ];

      const chosen = prng.pick(sets);
      const all = prng.shuffle([
        { ar: chosen.target_ar, en: chosen.target_en, isCorrect: true },
        ...chosen.distractors_ar.map((d, i) => ({ ar: d, en: chosen.distractors_en[i], isCorrect: false }))
      ]);
      const correctIndex = all.findIndex(it => it.isCorrect);

      const options = all.map((it, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: it.ar,
        text_en: it.en,
        value: it.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `حدد الكلمة الشاذة التي تختلف جوهريًا وفلسفيًا عن الكلمات الثلاث الأخرى:\n\n[ ${all.map(o => o.text_ar).join(' , ')} ]`,
        questionText_en: `Identify the categorical intruder that differs fundamentally in its ontological properties from the other three:\n\n[ ${all.map(o => o.text_en).join(' , ')} ]`,
        options,
        correctIndex,
        explanation_ar: `${chosen.exp_ar} الكلمة الشاذة هي "${chosen.target_ar}" (الخيار ${options[correctIndex].id}).`,
        explanation_en: `Evaluating ontological dimension isolates "${chosen.target_en}" (${options[correctIndex].id}).`
      };
    }
  },

  // 14. Contextual Polysemy Disambiguation
  {
    id: 'verbal_contextual_polysemy',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'القانون الفيزيائي والنظام الكوني الثابت', text_en: 'Physical natural law and universal regularity', isCorrect: true },
        { id: 'B', text_ar: 'طريقة الطبخ وإعداد الطعام', text_en: 'Culinary preparation recipe', isCorrect: false },
        { id: 'C', text_ar: 'العادات القبلية المتوارثة دون ضابط علمي', text_en: 'Tribal folklore traditions', isCorrect: false },
        { id: 'D', text_ar: 'العقوبة الجنائية القضائية', text_en: 'Judicial penal sentencing', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في السياق الفلسفي والعلمي لكتابات علماء الطبيعة: "تجري الكواكب في مداراتها وفق سُنّةٍ محكمة لا تتبدل".\nما هو المعنى الدلالي الدقيق لكلمة "سُنّة" في هذا السياق المعرفي؟`,
        questionText_en: `In scientific and philosophical literature: "Planets traverse their orbits following an immutable universal Sunnah (regularity)."\nWhat is the exact semantic connotation of the term in this rigorous context?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `كلمة "سُنّة" في سياق فلسفة العلوم والكونيات تعني القانون الطبيعي الموضوعي المطرد والنواميس الفيزيائية الثابتة التي تحكم المادة، وهو ما يطابق الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Contextual epistemology defines the term as invariant natural physical law, matching (${shuffled[correctIndex].id}).`
      };
    }
  },

  // 15. Structural Metaphorical Mapping
  {
    id: 'verbal_metaphorical_mapping',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const options = [
        { id: 'A', text_ar: 'الأفكار والمبادئ المعرفية الراسخة', text_en: 'Core foundational ideas and principles', isCorrect: true },
        { id: 'B', text_ar: 'الأوراق الإدارية والعقود الورقية', text_en: 'Administrative paper documents', isCorrect: false },
        { id: 'C', text_ar: 'الطلاء الخارجي للجدران', text_en: 'Exterior wall paint', isCorrect: false },
        { id: 'D', text_ar: 'أثاث المكاتب والمقاعد', text_en: 'Office desks and furniture', isCorrect: false }
      ];

      const shuffled = prng.shuffle(options);
      const correctIndex = shuffled.findIndex(o => o.isCorrect);

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الاستعارة الإدراكية القائلة: "النظرية العلمية بناءٌ معماري شامخ، والباحثون يضعون لبناته".\nإذا كان "البناء" يناظر "النظرية"، فما الذي تناظره "قواعد وأساسات البناء الراسخة تحت الأرض" في المجال المعرفي؟`,
        questionText_en: `In the conceptual metaphor: "A scientific theory is an architectural skyscraper, and researchers lay its bricks."\nIf the building maps to the theory, what do the "deep subterranean foundations" map to in cognitive domain?`,
        options: shuffled,
        correctIndex,
        explanation_ar: `الأساسات التي يرتكز عليها البناء المعماري تناظر دلاليًا المبادئ والبديهيات والمسلمات المنطقية الأساسية التي تقوم عليها النظرية العلمية، وهو ما يثبت صحة الخيار (${shuffled[correctIndex].id}).`,
        explanation_en: `Subterranean foundations conceptually map to fundamental axioms and empirical principles underpinning the theory, isolating (${shuffled[correctIndex].id}).`
      };
    }
  }
];
